import React, { useState, useEffect } from "react";
import IconButton from "components/common/iconButton";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  Col,
  Row,
  Spinner,
} from "react-bootstrap"
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import AdvanceTablePagination from "components/common/advance-table/advanceTablePagination";
import AdvanceTableFooter from 'components/common/advance-table/advanceTableFooter';
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import paths from "routes/paths";
import { useTranslation } from "react-i18next";
import axios from "axios"; // Import Axios
import { fetchRoleAndClaims, handleLoadingAndAccess, getInitialPageSize, formatCreatedBy } from "../../assets/js/common";

function Task() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  const [userList, setUserList] = useState([]);

  const handleViewDetails = (taskId) => {
    navigate(`${paths.taskDetails.replace(":taskId", taskId.toLowerCase())}`);
  };

  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "workTasks",
      claimValue: "view",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);

  const columns = [
    {
      accessorKey: "index",
      header: t("index"),
      cell: ({ row }) => <span>{row.original.Index}</span>  // +1 to make it 1-based index
    },
    {
      accessorKey: "taskName",
      header: t("taskName"),
      cell: ({ row }) => <span>{row.original.TaskName}</span>
    },
    {
      accessorKey: "subTaskNumber",
      header: t("subTasks"),
      cell: ({ row }) => <span>{row.original.SubtaskNumber}</span>
    },
    {
      accessorKey: "createdBy",
      header: t("createdByWithoutcolon"),
      cell: ({ row }) => <span>{row.original.CreatedBy}</span>
    }
  ];

  const table = useAdvanceTable({
    data,
    columns,
    selection: false,
    sortable: true,
    pagination: true,
    perPage: getInitialPageSize('taskTable')
  });

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
       const selectedCustomerId = localStorage.getItem("customerId");

       if (!selectedCustomerId) {
         setIsLoading(false);
         console.error("CustomerId is missing");
         return;
       }
        
      try {
        const [userRes, taskRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL}/userRoute/users/allLocations`,
            { 
              params: { customerId: selectedCustomerId },
              headers: {
                Authorization: process.env.REACT_APP_BasicAuthSecret
              }            
            }
          ),
          axios.get(`${process.env.REACT_APP_API_URL}/taskRoute/tasks`, {
            params: { customerId: selectedCustomerId },
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }),
        ]);

        const userList = userRes.data;
        const allTasks = taskRes.data;

        // const sortedTasks = allTasks.sort((a, b) =>
        //   new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
        // );

        const mappedTasks = allTasks.map((task) => {
          const user = userList.find((u) => u.UserId === task.CreatedBy);
          return {
            ...task,
            CreatedBy: user?.Name || formatCreatedBy(task.CreatedBy),
          };
        });

        const filtered = mappedTasks.filter((task) => {
          return (
            task.TaskName?.toLowerCase().includes(globalFilter.toLowerCase()) ||
            task.CreatedBy?.toLowerCase().includes(globalFilter.toLowerCase())
          );
        });

        setUserList(userList);
        setData(filtered);
      } catch (error) {
        console.error("Error fetching tasks or users:", error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [globalFilter]);

  const loadingOrRedirect = handleLoadingAndAccess(
    isLoading,
    hasAccess,
  );

  if (loadingOrRedirect) {
    return loadingOrRedirect; // Render the loading spinner or handle navigation
  }

  return (
    <>
      <AdvanceTableProvider {...table}>
        <Row className="g-3 mb-3">
          <Col lg={12} xxl={12}>
            <Card className="mb-3 h-100">
              <Background image={corner4} className="bg-card" />
              <Card.Body className="position-relative pe-3 pb-2">
                <Row className="flex-between-center">
                  <Col md>
                    <h5 className="mb-2 mb-md-0">{t("task")}</h5>
                  </Col>
                  <Col xs="auto">
                    <IconButton
                      as={Link}
                      to={paths.newTask}
                      variant="primary"
                      size="sm"
                      icon="plus"
                      transform="shrink-3"
                    >
                      <span className="ms-1">{t("newTask")}</span>
                    </IconButton>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="mb-3">
          <Card.Header>
            <Row className="flex-between-center">
              <Col xs={8} sm="auto" className="ms-auto text-end ps-0">
                <AdvanceTableSearchBox setGlobalFilter={setGlobalFilter} />
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                      <span className="visually-hidden">{t("loading")}</span>
                  </Spinner>
              </div>
            ) : (
              <>
                <AdvanceTable
                  headerClassName="bg-200 text-nowrap align-middle text-center"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    bordered: true,
                    striped: true,
                    size: "sm",
                    className: "mb-0 overflow-hidden data-font-size table-hover"
                  }}
                  getRowProps={(row) => ({
                    // onClick: () => handleViewDetails(),
                    onClick: () => handleViewDetails(row.original.TaskId),
                    style: { cursor: "pointer" }
                  })}
                />
                {data.length === 0 && (
                  <div className="text-center pt-4">
                    {t("noDataAvailableText")}
                  </div>
                )}
              </>
            )}
          </Card.Body>
          <Card.Footer>
            {/* <AdvanceTablePagination /> */}
            <AdvanceTableFooter
              rowInfo
              navButtons
              rowsPerPageSelection
              tableKey="taskTable"
            />
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>
    </>
  );
}

export default Task;